<script setup lang="ts">
    import type { ISbStoryData } from '@storyblok/js';
    import { useDatasourceEntries } from '~/composables/storyblok-proxy/useDatasourceEntries';

    const props = defineProps<{ blok: PageNewsStoryblok; canonicalStory?: ISbStoryData<unknown> }>();

    const { lang } = useLanguage();
    const { t } = useI18n();
    const route = useRoute();

    const { isPtaFamily, isFachportal } = usePlatformMeta();

    const newsRootPath = computed(() => {
        const segs = route.path.split('/').filter(Boolean);
        if (segs.length >= 2) segs.pop();
        return `/${segs.join('/')}`;
    });

    const canonicalSource = computed(() => {
        if (isResolvableStoryblokLink(props.blok.canonicalLink)) {
            return props.blok.canonicalLink;
        }
        if (props.canonicalStory?.full_slug) {
            return props.canonicalStory;
        }
        return undefined;
    });

    const canonicalUrlAbsolute = useCanonicalUrl(canonicalSource);
    const head = computed(() => {
        const seoTitle = props.blok.AI_SEO?.title ?? props.blok.seo?.title ?? '';

        return {
            title: `${seoTitle && seoTitle.length > 0 ? `${seoTitle} | ` : ''}News | ${
                isFachportal.value ? `${t('platform.prescriber')} | ` : ''
            }MEDICE Health Family`,
            htmlAttrs: {
                lang: lang.value
            },
            link: [{ rel: 'canonical' as const, href: canonicalUrlAbsolute.value }]
        };
    });

    useHead(head);

    // Fetch categories with translations from Storyblok
    const { data: datasourceData } = await useDatasourceEntries('news-categories');
    const datasourceEntries = computed(() => datasourceData.value || []);

    const resolveCategoryValue = (category: string | number) => {
        const matchedCategory = datasourceEntries.value.find((data) => data.value === category);

        // If found, return translated dimension_value, otherwise fallback to original value
        return matchedCategory ? matchedCategory.dimension_value || matchedCategory.value : category;
    };

    const resolveCategoryKey = (category: string | number) => {
        const matchedCategory = datasourceEntries.value.find((data) => data.value === category);
        return matchedCategory ? matchedCategory.name : category;
    };

    // Handle date formatting for the news article
    const date = new Date(props.blok.Date);
    const formattedDate = `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
</script>

<template>
    <div
        v-editable="blok"
        class="header-wrapper"
    >
        <div class="header">
            <div class="headline-wrapper">
                <!-- TODO: add to list for design system streamlining -->
                <h1
                    class="--font-size-l"
                    :class="isPtaFamily ? '--font-family-satisfy' : '--font-family-abril'"
                >
                    {{ props.blok.Headline }}
                </h1>
                <div class="categories">
                    <p>{{ formattedDate }}</p>
                    <ul v-if="props.blok.Categories?.length > 0">
                        <li
                            v-for="category in props.blok.Categories"
                            :key="category"
                        >
                            <NuxtLink :to="{ path: newsRootPath, query: { category: resolveCategoryKey(category) } }">
                                {{ resolveCategoryValue(category) }}
                            </NuxtLink>
                        </li>
                    </ul>
                </div>
            </div>
            <h2
                v-if="props.blok.Subheadline"
                class="--font-size-s --font-family-figtree --font-weight-500"
            >
                {{ props.blok.Subheadline }}
            </h2>
        </div>
    </div>

    <div class="content-wrapper">
        <StoryblokComponentWrapper
            v-for="element in props.blok.Body"
            :key="element._uid"
            :blok="element"
        />
    </div>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/news/news-page.scss';
    @import '~/assets/scss/headline/headline.scss';
</style>
